import { baseApi } from 'src/Services/api.ts'

import { PaginatedResponse } from '../types'

export interface Client {
  id: number
  name: string
  inn: string
  director: string
  legalAddress: string
  phone: string
  email?: string
  createdAt: string
  imageUrl: string
  organizationalForm: string
  ogrn?: string
  headPosition?: string
  headPositionGenitive?: string
  headNameGenitive?: string
  offerContactName?: string
  offerContactPosition?: string
  authorityType?: string
  kpp?: string
  rs?: string
  ks?: string
  bic?: string
  bankName?: string
  postAddress?: string
}

export interface ClientQueryParams {
  pageNumber?: number
  limit?: number
  searchString?: string
  all?: boolean
}

export interface AddClientBody {
  name: string
  inn: string
  director: string
  legalAddress: string
  phone: string
  email?: string
  organizationalForm: string
  ogrn?: string
  headPosition?: string
  headPositionGenitive?: string
  headNameGenitive?: string
  offerContactName?: string
  offerContactPosition?: string
  authorityType?: string
  kpp?: string
  rs?: string
  ks?: string
  bic?: string
  bankName?: string
  postAddress?: string
}

export const clientsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllClients: builder.query<
      PaginatedResponse<Client[]>,
      ClientQueryParams
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()

        queryParams.append('status', 'ACTIVE')

        if (params) {
          if (params.pageNumber)
            queryParams.append('page', params.pageNumber.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.searchString)
            queryParams.append('name', params.searchString)
          if (params.all) queryParams.append('all', 'true')
        }

        const queryString = queryParams.toString()
        return `/clients${queryString ? `?${queryString}` : ''}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,

      merge: (currentCache, newItems, meta) => {
        if (meta?.arg?.pageNumber === 1) {
          return {
            data: newItems?.data,
            meta: newItems.meta,
          }
        }
        return {
          data: [...currentCache.data, ...newItems.data],
          meta: newItems.meta,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
      providesTags: ['clients'],
    }),

    getClientById: builder.query<Client, number>({
      query: (id) => `/clients/${id}`,
      providesTags: ['clients'],
    }),

    addClient: builder.mutation<void, AddClientBody>({
      query: (data) => ({
        url: `/clients`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['clients'],
    }),

    updateClient: builder.mutation<
      void,
      { id: number; data: Partial<AddClientBody> }
    >({
      query: ({ id, data }) => ({
        url: `/clients/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['clients'],
    }),

    archiveClient: builder.mutation<void, number>({
      query: (id) => ({
        url: `/clients/${id}/archive`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['clients'],
    }),

    deleteClient: builder.mutation<void, number>({
      query: (id) => ({
        url: `/clients/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['clients'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetAllClientsQuery,
  useAddClientMutation,
  useUpdateClientMutation,
  useArchiveClientMutation,
} = clientsApi
